import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import logo from '../assets/logo.png';
import $ from 'jquery';
import 'datatables.net';
import 'datatables.net-dt';
import * as XLSX from 'xlsx';
import './TariffViewer.css';

/** Strip non-digits for HS code comparison (layout xxxx.xx.xx). */
const hsDigitsOnly = (value) => String(value || '').replace(/\D/g, '');

/** Format typed digits as xxxx.xx.xx (max 8 digits). */
const formatHsCodeFilter = (value) => {
    const digits = hsDigitsOnly(value).slice(0, 8);
    if (digits.length <= 4) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 4)}.${digits.slice(4)}`;
    return `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6)}`;
};

/** Match HS / parent HS against filter with or without dots. */
const hsCodeMatchesFilter = (hsCode, parentHsCode, filter) => {
    const trimmed = String(filter || '').trim().toLowerCase();
    if (!trimmed) return true;

    const filterDigits = hsDigitsOnly(trimmed);
    const hsLower = String(hsCode || '').toLowerCase();
    const parentLower = String(parentHsCode || '').toLowerCase();

    if (filterDigits.length >= 5) {
        return hsDigitsOnly(hsCode).includes(filterDigits)
            || hsDigitsOnly(parentHsCode).includes(filterDigits);
    }

    return hsLower.includes(trimmed) || parentLower.includes(trimmed);
};

/** Chapter header rows use 2-digit HS Code matching chapter number. */
const isChapterHeaderRow = (row) => {
    const chapter = String(row['Chapter'] || '').padStart(2, '0');
    const hsDigits = hsDigitsOnly(row['HS Code']);
    return hsDigits === chapter;
};

const buildChapterOptions = (tariffData) => {
    const labels = new Map();
    for (const row of tariffData) {
        const chapter = row['Chapter'];
        if (!chapter || labels.has(chapter) || !isChapterHeaderRow(row)) continue;
        const goods = String(row['Goods'] || '').trim();
        if (goods) labels.set(chapter, goods);
    }

    return [...new Set(tariffData.map((row) => row['Chapter']))]
        .sort()
        .map((chapter) => ({
            chapter,
            label: labels.get(chapter) || `CHAPTER ${parseInt(chapter, 10)}`,
        }));
};

const TariffViewer = () => {
    const tableRef = useRef(null);
    const sectionListRef = useRef(null);
    const dataTableRef = useRef(null);
    const [tariffData, setTariffData] = useState([]);
    const [sectionData, setSectionData] = useState([]);
    const [sectionNotesData, setSectionNotesData] = useState([]);
    const [chapterNotesData, setChapterNotesData] = useState([]);
    const [isTableInitialized, setIsTableInitialized] = useState(false);
    const [hsCodeFilter, setHsCodeFilter] = useState('');
    const [goodsFilter, setGoodsFilter] = useState('');
    const [chapterFilter, setChapterFilter] = useState('');
    const [sectionFilter, setSectionFilter] = useState('');
    const [isProDropdownOpen, setIsProDropdownOpen] = useState(false);
    const [showNotes, setShowNotes] = useState(false);
    const navigate = useNavigate();

    const expectedHeaders = useMemo(() => ['Chapter', 'HS Code', 'Stat Code', 'Unit', 'Goods', 'Rate#'], []);

    const applyStyles = () => {
        setTimeout(() => {
            const isDarkMode = $('body').hasClass('dark-mode');
            $('.dataTables_paginate .paginate_button').css({
                padding: '6px 12px',
                fontSize: '14px',
                fontWeight: '500',
                border: '1px solid ' + (isDarkMode ? '#777' : '#ced4da'),
                borderRadius: '4px',
                backgroundColor: isDarkMode ? '#555' : '#fff',
                color: isDarkMode ? '#ccc' : '#495057',
                cursor: 'pointer',
                textDecoration: 'none',
                boxSizing: 'border-box',
                margin: '0 4px',
                transition: 'background-color 0.2s, color 0.2s',
            });
            $('.dataTables_paginate .paginate_button:not(.disabled):hover').css({
                backgroundColor: isDarkMode ? '#666' : '#f0f4f8',
                color: isDarkMode ? '#fff' : '#007bff',
            });
            $('.dataTables_paginate .paginate_button.current').css({
                backgroundColor: isDarkMode ? '#007bff' : '#007bff',
                color: '#fff',
                borderColor: isDarkMode ? '#007bff' : '#007bff',
            });
            $('.dataTables_paginate .paginate_button.disabled').css({
                cursor: 'not-allowed',
                opacity: '0.5',
            });
        }, 0);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdown = document.querySelector('.pro-dropdown');
            if (dropdown && !dropdown.contains(event.target)) {
                setIsProDropdownOpen(false);
            }
        };

        if (isProDropdownOpen) {
            document.addEventListener('click', handleClickOutside, { capture: true });
        }

        return () => {
            document.removeEventListener('click', handleClickOutside, { capture: true });
        };
    }, [isProDropdownOpen]);

    useEffect(() => {
        const loadTariffData = async () => {
            if (tariffData.length > 0 && sectionData.length > 0 && sectionNotesData.length > 0 && chapterNotesData.length > 0) {
                initializeTable(tariffData, sectionData);
                return;
            }

            try {
                const response = await fetch('/AUTariff.xlsx');
                if (!response.ok) throw new Error('Failed to fetch the Excel file');

                const arrayBuffer = await response.arrayBuffer();
                const workbook = XLSX.read(arrayBuffer, { type: 'array' });

                // Load Section Data
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                const loadedSectionData = XLSX.utils.sheet_to_json(firstSheet);
                setSectionData(loadedSectionData);

                // Load Tariff Data
                const secondSheet = workbook.Sheets[workbook.SheetNames[1]];
                if (!secondSheet) throw new Error('No second sheet found');
                let loadedTariffData = XLSX.utils.sheet_to_json(secondSheet);

                let lastHsCode = '';
                loadedTariffData = loadedTariffData.map(row => {
                    const newRow = {};
                    expectedHeaders.forEach(header => {
                        if (header === 'Stat Code') {
                            const statCode = String(row[header] || '');
                            newRow[header] = statCode.length === 1 ? '0' + statCode : statCode;
                        } else if (header === 'Chapter') {
                            newRow[header] = String(row[header] || '').padStart(2, '0');
                        } else {
                            newRow[header] = row[header] !== undefined ? String(row[header]) : '';
                        }
                    });
                    newRow.isFourDigitHS = /^[0-9]{4}$/.test(row['HS Code']);
                    if (newRow['HS Code']) {
                        lastHsCode = newRow['HS Code'];
                    }
                    newRow.parentHsCode = lastHsCode;
                    return newRow;
                });
                setTariffData(loadedTariffData);

                // Load Section Notes with raw cell values
                const sectionNotesSheet = workbook.Sheets['Section Notes'];
                if (!sectionNotesSheet) throw new Error('No Section Notes sheet found');
                const sectionNotesRaw = XLSX.utils.sheet_to_json(sectionNotesSheet, { raw: true });
                console.log('Section Notes Raw:', sectionNotesRaw);
                setSectionNotesData(sectionNotesRaw);

                // Load Chapter Notes with raw cell values
                const chapterNotesSheet = workbook.Sheets['Chapter Notes'];
                if (!chapterNotesSheet) throw new Error('No Chapter Notes sheet found');
                const chapterNotesRaw = XLSX.utils.sheet_to_json(chapterNotesSheet, { raw: true });
                console.log('Chapter Notes Raw:', chapterNotesRaw);
                setChapterNotesData(chapterNotesRaw);

                initializeTable(loadedTariffData, loadedSectionData);
            } catch (error) {
                console.error('Error loading tariff data:', error);
                alert('Failed to load the tariff data or notes.');
            }
        };

        const initializeTable = (tariffData, sectionData) => {
            if (isTableInitialized) {
                applyStyles();
                return;
            }

            if (!$.fn.DataTable.isDataTable(tableRef.current)) {
                dataTableRef.current = $(tableRef.current).DataTable({
                    data: tariffData,
                    columns: expectedHeaders.map(header => ({ title: header, data: header })),
                    pageLength: 15,
                    lengthMenu: [10, 15, 25, 50, 100],
                    searching: true,
                    ordering: true,
                    order: [],
                    responsive: true,
                    language: {
                        search: '',
                        searchPlaceholder: 'Filter records...',
                        lengthMenu: 'Show _MENU_ entries',
                    },
                    createdRow: (row, data) => {
                        if (data.isFourDigitHS) $(row).addClass('bold-row');
                    },
                    dom: '<"table-header"l><"notes-panel-placeholder"t><"table-footer"ip>',
                });

                setIsTableInitialized(true);
                applyStyles();

                dataTableRef.current.on('draw', () => {
                    applyStyles();
                });

                const sectionList = sectionListRef.current;
                sectionList.innerHTML = '';
                sectionData.forEach(section => {
                    const li = document.createElement('li');
                    li.textContent = `${section['Section']} - ${section['Section title']} (${section['Chapter'] || 'N/A'})`;

                    const chapterRange = String(section['Chapter'] || '');
                    if (chapterRange) {
                        li.addEventListener('click', () => {
                            setSectionFilter(chapterRange);
                            setChapterFilter('');
                            const firstChapter = chapterRange.includes('-') ? chapterRange.split('-')[0] : chapterRange;
                            const targetRowIndex = tariffData.findIndex(row => row['Chapter'] === firstChapter.padStart(2, '0'));
                            if (targetRowIndex !== -1) {
                                setTimeout(() => {
                                    const table = dataTableRef.current;
                                    const row = table.row(targetRowIndex).node();
                                    if (row) {
                                        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        $(row).css('background-color', '#ffff99');
                                        setTimeout(() => $(row).css('background-color', ''), 2000);
                                    }
                                }, 100);
                            }
                        });
                    } else {
                        li.classList.add('disabled');
                        li.title = 'Invalid chapter range';
                    }
                    sectionList.appendChild(li);
                });
            }
        };

        loadTariffData();

        return () => {
            if (dataTableRef.current) {
                dataTableRef.current.off('draw');
                $.fn.dataTable.ext.search = [];
            }
        };
    }, [tariffData, sectionData, sectionNotesData, chapterNotesData, isTableInitialized, expectedHeaders]);

    const chapterOptions = useMemo(() => buildChapterOptions(tariffData), [tariffData]);

    useEffect(() => {
        if (!dataTableRef.current) return;

        $.fn.dataTable.ext.search = [];
        $.fn.dataTable.ext.search.push(function (settings, data, dataIndex, rowData) {
            if (settings.nTable.id !== 'tariffTable') return true;

            const hsCode = String(data[1] || '').toLowerCase();
            const parentHsCode = String((rowData && rowData.parentHsCode) || '').toLowerCase();
            const goods = String(data[4] || '').toLowerCase();
            const chapter = data[0] || '';
            const hsFilter = hsCodeFilter.toLowerCase().trim();
            const goodsFilterValue = goodsFilter.toLowerCase().trim();
            const chapterFilterValue = chapterFilter;
            const sectionFilterValue = sectionFilter;

            let hsCodeMatch = false;
            let goodsMatch = true;
            let chapterSectionMatch = true;

            if (hsFilter) {
                hsCodeMatch = hsCodeMatchesFilter(hsCode, parentHsCode, hsFilter);
            } else {
                hsCodeMatch = true;
            }

            if (goodsFilterValue) {
                goodsMatch = goods.includes(goodsFilterValue);
            }

            if (chapterFilterValue) {
                chapterSectionMatch = chapter === chapterFilterValue;
            } else if (sectionFilterValue) {
                const chapterRanges = sectionFilterValue.includes('-') ? sectionFilterValue.split('-').map(Number) : [Number(sectionFilterValue)];
                const minChapter = chapterRanges[0];
                const maxChapter = chapterRanges[1] || minChapter;
                const chapterNum = parseInt(chapter, 10);
                chapterSectionMatch = !isNaN(chapterNum) && chapterNum >= minChapter && chapterNum <= maxChapter;
            }

            return hsCodeMatch && goodsMatch && chapterSectionMatch;
        });

        const timeout = setTimeout(() => {
            dataTableRef.current.draw();
        }, 400);

        return () => clearTimeout(timeout);
    }, [hsCodeFilter, goodsFilter, chapterFilter, sectionFilter]);

    const handleLogout = () => {
        signOut(auth)
            .then(() => {
                navigate('/');
            })
            .catch((err) => console.error('Logout error:', err));
    };

    const resetTable = () => {
        if (dataTableRef.current) {
            setHsCodeFilter('');
            setGoodsFilter('');
            setChapterFilter('');
            setSectionFilter('');
            setShowNotes(false);
            $.fn.dataTable.ext.search = [];
            dataTableRef.current.search('').draw();
            dataTableRef.current.page(0).draw('page');
            applyStyles();
        }
    };

    const handleHsCodeFilterChange = (e) => {
        setHsCodeFilter(formatHsCodeFilter(e.target.value));
    };

    const handleGoodsFilterChange = (e) => {
        setGoodsFilter(e.target.value);
    };

    const handleChapterFilterChange = (e) => {
        setChapterFilter(e.target.value);
        setSectionFilter('');
    };

    const toggleNotes = () => {
        setShowNotes(prev => !prev);
    };

    // Get notes for the current chapter, preserving line breaks
    const getCurrentNotes = () => {
        let currentChapter = chapterFilter;
        if (!currentChapter && sectionFilter) {
            const chapterRange = sectionFilter.includes('-') ? sectionFilter.split('-')[0] : sectionFilter;
            currentChapter = String(chapterRange).padStart(2, '0');
        }

        if (!currentChapter) {
            return { sectionNotes: 'Select a chapter or section to view notes.', chapterNotes: '' };
        }

        // Find section for the current chapter
        const section = sectionData.find(s => {
            const range = s['Chapter'] ? String(s['Chapter']).split('-').map(Number) : [];
            const chapterNum = parseInt(currentChapter, 10);
            return range.length === 2 ? chapterNum >= range[0] && chapterNum <= range[1] : chapterNum === parseInt(s['Chapter'], 10);
        });

        const sectionNotes = section
            ? sectionNotesData.find(note => note['Section'] === section['Section'])?.['Section Notes'] || 'No section notes available.'
            : 'No section found for this chapter.';

        const chapterNotes = chapterNotesData.find(note => note['Chapter'] === currentChapter)?.['Chapter Notes'] || 'No chapter notes available.';

        return { sectionNotes, chapterNotes };
    };

    const { sectionNotes, chapterNotes } = getCurrentNotes();

    return (
        <div className="tariff-viewer-container">
            <header className="header">
                <div className="header-content">
                    <img src={logo} alt="Customs Pro AI Logo" className="header-logo" />
                    <h1>Customs Schedule 3 Tariff</h1>
                    <nav className="nav">
                        <Link to="/" className="nav-item">Home</Link>
                        <Link to="/app" className="nav-item">Classify Goods</Link>
                        <Link to="/customs-chat" className="nav-item">Customs Chat</Link>
                        <div
                            className={`nav-item pro-dropdown ${isProDropdownOpen ? 'open' : ''}`}
                            onClick={() => setIsProDropdownOpen(!isProDropdownOpen)}
                        >
                            <span className="active">Pro</span>
                            <div className="dropdown-content" onClick={(e) => e.stopPropagation()}>
                                <Link to="/tariff-viewer" className="dropdown-item active" onClick={() => setIsProDropdownOpen(false)}>Tariff Viewer</Link>
                                <Link to="/tco-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>TCO Viewer</Link>
                                <Link to="/precedent-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Precedent Viewer</Link>
                                <Link to="/ahecc-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>AHECC Viewer</Link>
                                <Link to="/schedule4-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Schedule 4 Viewer</Link>
                                <Link to="/refund-reason-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Refund Reason Codes</Link>
                                <Link to="/gst-exemptions-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>GST Exemption Codes</Link>
                                <Link to="/customs-depots-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Customs Depot Codes</Link>
                                <Link to="/quarantine-depots-viewer" className="dropdown-item"onClick={() => setIsProDropdownOpen(false)}>Quarantine Depot Codes</Link>
                                <Link to="/history" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>History</Link>
                            </div>
                        </div>
                        <Link to="/settings" className="nav-item">Settings</Link>
                        <span className="nav-item logout" onClick={handleLogout} role="button">Logout</span>
                    </nav>
                </div>
            </header>
            <div className="main-content">
                <div className="sections-panel">
                    <h2>Sections</h2>
                    <ul ref={sectionListRef}></ul>
                </div>
                <div className="table-panel">
                    <div className="table-header">
                        <div id="customFilter">
                            <select
                                value={chapterFilter}
                                onChange={handleChapterFilterChange}
                                className="chapter-filter-select"
                            >
                                <option value="">All Chapters</option>
                                {chapterOptions.map(({ chapter, label }) => (
                                    <option key={chapter} value={chapter}>{label}</option>
                                ))}
                            </select>
                            <input
                                type="text"
                                placeholder="Filter HS Code (e.g. 85414300)..."
                                value={hsCodeFilter}
                                onChange={handleHsCodeFilterChange}
                                className="custom-filter-input"
                            />
                            <input
                                type="text"
                                placeholder="Filter Goods..."
                                value={goodsFilter}
                                onChange={handleGoodsFilterChange}
                                className="custom-filter-input"
                            />
                            <button
                                onClick={toggleNotes}
                                className="notes-toggle-btn"
                            >
                                {showNotes ? 'Hide Notes' : 'View Notes'}
                            </button>
                        </div>
                        <button id="resetTableBtn" onClick={resetTable}>Reset Table</button>
                    </div>
                    {showNotes && (
                        <div className="notes-panel">
                            <h2>Notes for Chapter {chapterFilter || (sectionFilter ? sectionFilter.split('-')[0] : 'N/A')}</h2>
                            <h3>Section Notes</h3>
                            <p>{sectionNotes}</p>
                            <h3>Chapter Notes</h3>
                            <p>{chapterNotes}</p>
                        </div>
                    )}
                    <div className="table-container">
                        <table id="tariffTable" className="display" ref={tableRef}></table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TariffViewer;